import React, { useCallback, useMemo } from "react";
import { AHIconType, AHSeparator, AHText, Box } from "arkh";
import {
  Popover,
  PopoverList,
  PopoverListItem,
  PopoverListItemTitle,
} from "../../components/mobile/Popover";
import { FormattedMessage } from "react-intl";
import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { UserLoggedInNavigatorParamList } from "../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import {
  checkForUpdateAsync,
  fetchUpdateAsync,
  reloadAsync,
 useUpdates } from "expo-updates";
import Constants from "expo-constants";
import { Platform } from "react-native";
import { DateTime } from "luxon";

type ItemType =
  | {
      id: string;
      title: React.ReactNode;
      icon: AHIconType;
      onPress(): void;
      type: "item";
    }
  | { type: "separator" };

export function TeamHelpPopover(props: {
  onClose(): void;
  hasHelp: boolean;
  navigation: NativeStackNavigationProp<UserLoggedInNavigatorParamList>;
  top: number;
}) {
  const intl_tokens = useIntlTokens();
  const data: ItemType[] = useMemo(() => {
    return [
      props.hasHelp
        ? {
            id: "help",
            title: intl_tokens.Help,
            icon: "help",
            onPress: () => {
              props.navigation.navigateDeprecated("WebView", {
                url: "https://www.storesprint.com/support",
              });
              props.onClose();
            },
            type: "item",
          }
        : null,
      {
        id: "storesprint-updates",
        title: (
          <FormattedMessage
            defaultMessage="StoreSprint updates"
            description="TeamMoreMenu Store"
          />
        ),
        icon: "storesprint-update",
        type: "item",
        onPress: () => {
          props.navigation.navigateDeprecated("WebView", {
            url: "https://www.storesprint.com/in-app-update",
          });
          props.onClose();
        },
      },
      {
        id: "feedback",
        title: "Feedback",
        type: "item",
        icon: "feedback",
        onPress: () => {
          props.navigation.navigateDeprecated("WebView", {
            url: "https://www.storesprint.com/support/feedback",
          });
          props.onClose();
        },
      },
    ].filter((x) => x !== null) as ItemType[];
  }, [intl_tokens.Help, props]);

  const renderItem = useCallback(
    ({ item }: { item: ItemType }) =>
      item.type === "separator" ? (
        <AHSeparator spaceVertical="small" />
      ) : (
        <PopoverListItem leftIcon={item.icon} onPress={item.onPress}>
          <PopoverListItemTitle>{item.title}</PopoverListItemTitle>
        </PopoverListItem>
      ),
    []
  );

  return (
    <Popover onClose={props.onClose} top={props.top}>
      <PopoverList
        data={data}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        ListFooterComponent={Platform.OS === "web" ? null : <UpdateButton />}
      />
    </Popover>
  );
}

function keyExtractor(item: ItemType, index: number) {
  if (item.type === "separator") {
    return `separator${index}`;
  }
  return item.id;
}

function UpdateButton() {
  const updateInfo = useUpdates();

  const createdAt = updateInfo.currentlyRunning.createdAt;

  const version = Constants.expoConfig?.version;
  const createdAtString = createdAt
    ? DateTime.fromJSDate(createdAt).toUTC().startOf("minute").toISO({
        includeOffset: true,
        suppressSeconds: true,
      })
    : "";

  const embeddedLaunch = updateInfo.currentlyRunning.isEmbeddedLaunch
    ? "E"
    : "";
  const emergencyLaunch = updateInfo.currentlyRunning.isEmergencyLaunch
    ? "!"
    : "";
  const versionString = `${version}-${createdAtString}:${embeddedLaunch}${emergencyLaunch}`;

  if (updateInfo.isUpdatePending || updateInfo.isUpdateAvailable) {
    return (
      <PopoverListItem
        leftIcon="update"
        detail="none"
        onPress={() => {
          if (updateInfo.isUpdatePending) {
            reloadAsync();
          } else {
            fetchUpdateAsync().then(() => {
              reloadAsync();
            });
          }
        }}
      >
        <Box style={{ flexDirection: "column", flex: 1 }}>
          <PopoverListItemTitle>
            <FormattedMessage
              defaultMessage="Update available"
              description="ProfileScreen:UpdateButton:title"
            />
          </PopoverListItemTitle>
        </Box>
      </PopoverListItem>
    );
  }

  if (updateInfo.isDownloading) {
    return (
      <PopoverListItem leftIcon="loading" onPress={() => {}} detail="none">
        <Box style={{ flexDirection: "column", flex: 1 }}>
          <PopoverListItemTitle>
            <FormattedMessage
              defaultMessage="Downloading update"
              description="ProfileScreen:UpdateButton:downloading"
            />
          </PopoverListItemTitle>
        </Box>
      </PopoverListItem>
    );
  }

  return (
    <PopoverListItem
      leftIcon={
        updateInfo.isDownloading || updateInfo.isChecking ? "loading" : "update"
      }
      detail="none"
      onPress={() => {
        checkForUpdateAsync().then((r) => {
          if (r.isAvailable) {
            fetchUpdateAsync();
          }
        });
      }}
    >
      <Box style={{ flexDirection: "column", flex: 1 }}>
        <PopoverListItemTitle>
          <FormattedMessage
            defaultMessage="Check for updates"
            description="ProfileScreen:UpdateButton:check"
          />
        </PopoverListItemTitle>
        <AHText color="washedText" type="detail">
          {versionString}
        </AHText>
      </Box>
    </PopoverListItem>
  );
}
